import { useRef, useState } from 'react';
import { DateTime } from 'luxon';
import flatten from 'lodash/flatten';
import intersectionBy from 'lodash/intersectionBy';
import union from 'lodash/union';
import type { ITrafficSituation } from '../../../models/traffic-situation.model';
import TrafficSituationApiModel from '../models/traffic-situation.api-model';
import { TransportMode } from '../../../models/transport-mode.enum';
import TransportModeTrafficInformation from './components/TransportModeTrafficInformation';
import type { IMunicipality } from '../../../models/municipality.model';
import { ClientSideStorageItems, ClientSideStorageService } from '../../../services/client-side-storage.service';
import { ConfigurationService } from '../../../services/configuration.service';
import { TrafficSituationHelper } from '../../../helpers/traffic-situation.helper';
import MunicipalitySelector, { type IMunicipalitySelector } from '../../Shared/MunicipalitySelector';
import { MunicipalitySelectorLayout } from '../../Shared/MunicipalitySelector/municipality-selector-layout.enum';
import { AnalyticsService } from '../../../services/analytics-service';
import { SanitizerService } from '../../../services/sanitizer.service';
import useFetch from '../../../hooks/fetch.hook';
import { useTexts } from '../../../hooks/texts.hook';

function createDeepLinkToTrafficSituationsPage(trafficSituationsPageUrl: string, transportMode: TransportMode, municipalities: IMunicipality[]): string {
  return `${trafficSituationsPageUrl}?t=${transportMode}${municipalities.map((m) => `&k=${m.gid}`).join('')}&g=current`;
}

function filterBasedOnMunicipalities(ts: ITrafficSituation, selectedMunicipalities: IMunicipality[]): boolean {
  if (!selectedMunicipalities || selectedMunicipalities.length === 0) return true;

  const allAffectedMunicipalities = union(
    flatten(TrafficSituationHelper.getDistinctLines(ts).map((x) => x.municipalities ?? [])),
    ts.affectedStopPoints.filter((sp) => sp.municipality).map((sp) => sp.municipality!),
  );

  return intersectionBy(allAffectedMunicipalities, selectedMunicipalities, (m) => m.gid).length > 0;
}

function filterBasedOnTransportMode(ts: ITrafficSituation, selectedTransportModes: TransportMode[]): boolean {
  if ((ts.affectedLines === undefined || ts.affectedLines.length === 0) && (ts.affectedJourneys === undefined || ts.affectedJourneys.length === 0)) return true;
  return selectedTransportModes.some((tm) => TrafficSituationHelper.affectsLineWithTransportMode(ts, tm));
}

function createUpdatedString(updateTime: DateTime, t: (sv: string, en: string) => string): string {
  const translationUpdated = t('Uppdaterad', 'Updated');
  const isUpdatedToday = updateTime.hasSame(DateTime.now(), 'day');
  if (isUpdatedToday) return `${translationUpdated} ${updateTime.toLocaleString(DateTime.TIME_SIMPLE)}`;
  return `${translationUpdated} ${updateTime.toLocaleString(DateTime.DATETIME_SHORT)}`;
}

function getMunicipalitiesFromLocalStorage(): IMunicipality[] {
  try {
    const value = JSON.parse(ClientSideStorageService.getLocalStorage(ClientSideStorageItems.LocalStorage.TrafficInformationOverviewMunicipality)!);
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function processReceivedData(data: TrafficSituationApiModel[]): ITrafficSituation[] {
  return data?.map((ts) => TrafficSituationApiModel.mapToBusinessModel(ts)) ?? [];
}

export interface ITrafficInformationOverviewTexts {
  feedbacktext: string;
}

interface ITrafficInformationOverviewProps {
  texts: ITrafficInformationOverviewTexts;
}

function TrafficInformationOverview({ texts }: ITrafficInformationOverviewProps) {
  // === HOOKS ===
  const { t } = useTexts();

  // === CONSTANTS ===
  const transportModes = [TransportMode.tram, TransportMode.bus, TransportMode.train, TransportMode.ferry];
  const trafficSituationsPageUrl = ConfigurationService.get('trafficSituationsPageUrl');

  // === STATE ===
  const [{ status: getTrafficSituationsStatus, updated: getTrafficSituationsUpdated, data: getTrafficSituationsData }] = useFetch<TrafficSituationApiModel[]>('/api/trafficsituations/all');
  const [selectedMunicipalities, setSelectedMunicipalities] = useState<IMunicipality[]>(getMunicipalitiesFromLocalStorage());

  // === REFS ===
  const municipalitySelectorRef = useRef<IMunicipalitySelector>(null);

  // === VARIABLES ===
  const trafficSituations = processReceivedData(getTrafficSituationsData ?? []);
  const currentTrafficSituations = trafficSituations
    .filter((ts) => ts.startTime.startOf('day') <= DateTime.now().startOf('day') && (!ts.endTime || ts.endTime.startOf('day') >= DateTime.now().startOf('day')))
    .filter((ts) => filterBasedOnMunicipalities(ts, selectedMunicipalities ?? []));
  const newCurrentTrafficSituations = currentTrafficSituations.filter((ts) => ts.startTime > DateTime.now().minus({ hours: 24 }));

  const transportModesHasTwoDigitsNewAndOld = transportModes.some((transportMode) => {
    const newTrafficSituationsForTransportMode = newCurrentTrafficSituations.filter((ts) => filterBasedOnTransportMode(ts, [transportMode]));
    const totalTrafficSituationsForTransportMode = currentTrafficSituations.filter((ts) => filterBasedOnTransportMode(ts, [transportMode]));
    return newTrafficSituationsForTransportMode.length > 9 && totalTrafficSituationsForTransportMode.length - newTrafficSituationsForTransportMode.length > 9;
  });

  // === EVENT HANDLERS ===
  function handleMunicipalitySelectorChange(municipalities: IMunicipality[]): void {
    if (municipalities?.length > 0) {
      ClientSideStorageService.setLocalStorage(ClientSideStorageItems.LocalStorage.TrafficInformationOverviewMunicipality, JSON.stringify(municipalities));
    } else {
      ClientSideStorageService.clearLocalStorage(ClientSideStorageItems.LocalStorage.TrafficInformationOverviewMunicipality);
    }
    setSelectedMunicipalities(municipalities);
    AnalyticsService.addEvent(AnalyticsService.TrafficInformationOverview, 'Sökalternativ - Kommun', municipalities.map((m) => m.name).toString());
    municipalitySelectorRef.current?.clear();
  }

  function handleTransportModeClick(transportMode: TransportMode): void {
    AnalyticsService.addEvent(AnalyticsService.TrafficInformationOverview, 'Klick på trafikslag', transportMode);
  }

  return (
    <div>
      <div className="mb-3">
        <label htmlFor="municipality-selector" className="visually-hidden">
          {t('Kommun', 'Municipality')}
        </label>
        <MunicipalitySelector
          id="municipality-selector"
          ref={municipalitySelectorRef}
          placeholderText={t('Sök kommun', 'Search municipality')}
          noResultsText={t('Inga sökresultat', 'No search results')}
          layout={MunicipalitySelectorLayout.horizontal}
          commonSearches={[
            { gid: '9081014110001480', name: 'Göteborg', shortName: 'Göteborg' },
            { gid: '9081014110001490', name: 'Borås', shortName: 'Borås' },
            { gid: '9081014110001481', name: 'Mölndal', shortName: 'Mölndal' },
            { gid: '9081014110001488', name: 'Trollhättan', shortName: 'Trollhättan' },
            { gid: '9081014110001496', name: 'Skövde', shortName: 'Skövde' },
            { gid: '9081014110001485', name: 'Uddevalla', shortName: 'Uddevalla' },
          ]}
          onChange={handleMunicipalitySelectorChange}
          selectedMunicipalities={selectedMunicipalities}
        />
      </div>
      <div className="mb-3">
        <div className="row row-cols-1 row-cols-md-2 row-gap-2" role="list">
          {transportModes.map((transportMode) => {
            const trafficSituationsForTransportMode = currentTrafficSituations.filter((ts) => filterBasedOnTransportMode(ts, [transportMode]));
            return (
              <div className="col" key={transportMode} role="listitem">
                <a
                  href={createDeepLinkToTrafficSituationsPage(trafficSituationsPageUrl!, transportMode, selectedMunicipalities)}
                  className="d-block text-decoration-none"
                  onClick={() => {
                    handleTransportModeClick(transportMode);
                  }}
                >
                  <TransportModeTrafficInformation
                    transportMode={transportMode}
                    trafficSituations={trafficSituationsForTransportMode}
                    status={getTrafficSituationsStatus}
                    hideArrowIcon={transportModesHasTwoDigitsNewAndOld}
                  />
                </a>
              </div>
            );
          })}
        </div>
      </div>
      <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between mt-2">
        <span className="mb-2 mb-sm-0">
          {newCurrentTrafficSituations.length > 0 ? `${t('Nya störningar senaste dygnet', 'New disruptions in the past 24 hours')} - ` : ''} {createUpdatedString(getTrafficSituationsUpdated, t)}
        </span>
        <div className="ms-sm-2 last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(texts.feedbacktext ?? '') }}></div>
      </div>
    </div>
  );
}

export default TrafficInformationOverview;
